/**
 * The production line. Section 4.1.
 *
 * The string values here travel on the wire and are the same values the .NET
 * entities carry. Changing one is a breaking API change, not a rename.
 */

import { categoryOf } from '../catalog/catalog';

export const STAGE_ORDER = [
  'intake',
  'cadcam',
  'plaster',
  'wax',
  'ceramic',
  'finalcheck',
  'ready',
] as const;

export type Stage = (typeof STAGE_ORDER)[number];

export const STAGE_LABEL: Record<Stage, string> = {
  intake: 'Intake',
  cadcam: 'CAD / CAM',
  plaster: 'Plaster / Model',
  wax: 'Wax & Fitting',
  ceramic: 'Ceramic',
  finalcheck: 'Final Check',
  ready: 'Ready',
};

/*
 * There is no bench capacity.
 *
 * A number of trays a stage was said to hold came off the prototype's STAGES
 * table, and the lab has asked for it gone: nobody sets it, nobody checks it,
 * and a bench does not turn work away at ten. Everything it was used for --
 * a load percentage, a "room for 8" line under each card -- said less than the
 * count beside it already says.
 */

export type InputType = 'digital' | 'impression';

export const INPUT_TYPE_LABEL: Record<InputType, string> = {
  digital: 'Digital scan',
  impression: 'Impression',
};

export function isStage(value: string): value is Stage {
  return (STAGE_ORDER as readonly string[]).includes(value);
}

export function isInputType(value: string): value is InputType {
  return value === 'digital' || value === 'impression';
}

/**
 * The bench route a case takes, from the client's own description of the floor.
 *
 * Three routes, not two, and the entry bench is no longer the whole story:
 *
 *   digital, any item      intake, cadcam, wax, plaster, ceramic
 *   impression + zircon    intake, plaster, cadcam, wax, ceramic
 *   impression, all else   intake, plaster, wax, ceramic
 *
 * Two things here look wrong and are not. A digital case visits Plaster AFTER
 * Wax, because the model is produced once the wax work is done rather than
 * before it. And an impression case can still reach CAD / CAM: a zircon coping
 * is designed and milled from the poured model, which is why zircon takes a
 * longer road than a veneer does.
 *
 * The zircon branch is decided from the price list category rather than asked
 * at intake, so reception cannot send a case down the wrong road by mistake.
 * Final Check and Ready close every route.
 */
export function routeFor(input: InputType, product: string): Stage[] {
  return routeForCategory(input, categoryOf(product));
}

/**
 * The same route, from the item's category. A renamed item keeps its category
 * though its new name is on no list this module knows, so anything that has
 * the category should ask with it.
 */
export function routeForCategory(input: InputType, category: string): Stage[] {
  if (input === 'digital') {
    return ['intake', 'cadcam', 'wax', 'plaster', 'ceramic', 'finalcheck', 'ready'];
  }

  if (category === 'Zircon') {
    return ['intake', 'plaster', 'cadcam', 'wax', 'ceramic', 'finalcheck', 'ready'];
  }

  return ['intake', 'plaster', 'wax', 'ceramic', 'finalcheck', 'ready'];
}

/**
 * The production LINE shown on the floor board is all seven stages side by
 * side, because a flagged case can jump to any of them. Do not model the board
 * as a fork; the client explicitly asked for one straight line.
 */
export const LINE: Stage[] = [...STAGE_ORDER];

export function isOnRoute(stage: Stage, input: InputType, product: string): boolean {
  return routeFor(input, product).includes(stage);
}

/**
 * The stage a normal completion advances to.
 *
 * Returns null at the end of the route, and null when the kit is sitting on a
 * stage that is not on its route at all, which happens after a flag or a QC
 * rework. In both cases the caller must supply the destination explicitly;
 * section 7.4 makes `nextStage` required on the command for exactly those kits.
 */
export function nextStage(
  current: Stage,
  input: InputType,
  product: string,
  category: string = categoryOf(product),
): Stage | null {
  const route = routeForCategory(input, category);
  const index = route.indexOf(current);
  if (index === -1) return null;
  return route[index + 1] ?? null;
}

/**
 * The departments a flagged or failed case may be sent to. Intake is not
 * offered, because a case cannot be un-received, and Ready is not offered,
 * because reaching Ready is what Final Check decides.
 */
export const FLAG_DESTINATION_STAGES: readonly Stage[] = [
  'cadcam',
  'plaster',
  'wax',
  'ceramic',
  'finalcheck',
];

export function flagDestinations(current: Stage): Stage[] {
  return FLAG_DESTINATION_STAGES.filter((stage) => stage !== current);
}

/**
 * Why a case may not be sent by hand from `from` to `to`, word for word as the
 * server says it. Ready is what Final Check decides, so neither a flag nor a
 * rework nor a named Mark done reaches it from anywhere else; the backend's
 * Destinations class is the same rule.
 */
export const DESTINATION_REFUSAL = {
  sameStation: 'That case is already at this station. Pick the next one.',
  toIntake: 'A case cannot go back to Intake.',
  toReady: 'Only Final Check sends a case to Ready.',
} as const;

/** The refusal for a flag or a Final Check rework, or null when the move is allowed. */
export function flagRefusal(from: Stage, to: Stage): string | null {
  if (to === from) return DESTINATION_REFUSAL.sameStation;
  if (to === 'intake') return DESTINATION_REFUSAL.toIntake;
  if (to === 'ready') return DESTINATION_REFUSAL.toReady;
  return null;
}

/**
 * The refusal for a Mark done that names its next station: the flag rule,
 * except that Final Check may name Ready, which is finishing the case.
 */
export function completionRefusal(from: Stage, to: Stage): string | null {
  return from === 'finalcheck' && to === 'ready' ? null : flagRefusal(from, to);
}
